/**
 * sceneLoadStatus.js — load progress for CAD models, per model URL.
 *
 * The scene writes (download bytes, preparing, ready, failed); the DOM overlay
 * reads through useSceneLoad(). A module store instead of React state so byte
 * progress events re-render only the overlay, never the whole exploded view.
 *
 * Keyed by URL because React runs child effects before parent effects: a
 * cached model can report "ready" before anything upstream could "start" it.
 */

import { useSyncExternalStore } from 'react';

const BASE_URL = (typeof import.meta !== 'undefined' && import.meta.env
  && import.meta.env.BASE_URL) || '/';

/**
 * The model URL a manifest resolves to. Must match ExplodedScene's joinUrl()
 * exactly, since statuses are keyed by this string.
 */
export const modelUrlFor = (manifest) =>
  `${BASE_URL}${String(manifest?.model ?? 'cad/ace_ev_synthetic.glb').replace(/^\/+/, '')}`;

const statuses = new Map(); // url -> { phase, loaded, total, parts }
const subscribers = new Set();
let snapshot = new Map(statuses);
const lastProgressAt = new Map();

function update(url, patch) {
  if (!url) return;
  const prev = statuses.get(url) ?? { phase: 'downloading', loaded: 0, total: 0, parts: 0 };
  statuses.set(url, { ...prev, ...patch });
  snapshot = new Map(statuses);
  for (const fn of subscribers) fn();
}

export const sceneLoad = {
  /** Byte progress from the loader. Throttled to ~10 updates/s. */
  progress(url, loaded, total) {
    const cur = statuses.get(url);
    if (cur && cur.phase !== 'downloading') return;
    const now = performance.now();
    const done = total > 0 && loaded >= total;
    if (!done && now - (lastProgressAt.get(url) ?? 0) < 100) return;
    lastProgressAt.set(url, now);
    update(url, done
      ? { phase: 'preparing', loaded, total }
      : { phase: 'downloading', loaded, total });
  },
  preparing(url, parts) {
    const cur = statuses.get(url);
    if (cur?.phase === 'ready') return;
    update(url, { phase: 'preparing', parts });
  },
  ready(url) { update(url, { phase: 'ready' }); },
  failed(url) { update(url, { phase: 'error' }); },
  /** Forget a URL so a retry starts from "downloading" again. */
  reset(url) {
    if (!statuses.has(url)) return;
    statuses.delete(url);
    lastProgressAt.delete(url);
    snapshot = new Map(statuses);
    for (const fn of subscribers) fn();
  },
};

function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** Status for one model URL; unknown URLs read as an unstarted download. */
export function useSceneLoad(url) {
  const map = useSyncExternalStore(subscribe, () => snapshot);
  if (!url) return null;
  return map.get(url) ?? { phase: 'downloading', loaded: 0, total: 0, parts: 0 };
}
